/**
 * CartWatcher class handles cart state management and enforces quantity limits
 *
 * Key features:
 * - Monitors cart changes via network requests and localStorage
 * - Enforces maximum limits:
 *   - 3 units per individual product
 *   - 10 total items across the cart
 * - Maintains order of added items using a variant stack
 * - Emits events when cart changes occur
 * - Automatically removes excess items based on limits
 */
class CartWatcher {
  constructor() {
    // Maximum allowed quantity of any single product
    this.MAX_QUANTITY_PER_PRODUCT = 3;
    // Maximum total items allowed in cart
    this.MAX_TOTAL_PRODUCTS = 10;
    // Stack to track order of added variants for LIFO removal
    this.variantStack = [];
    // Cache for cart data
    this.cartCache = null;
    this.lastFetch = 0;
    this.CACHE_TTL = 2000; // 2 seconds cache TTL

    // internal: debounce + single-toast guard
    this._enforceTimer = null;
    this._TOAST_COOLDOWN_MS = 1500;
    this._lastToastAt = 0;
  }

  /**
   * Initializes the cart watcher:
   * 1. Fetches current cart state
   * 2. Rebuilds variant stack from cart items
   * 3. Stores initial cart state
   * 4. Sets up change monitoring
   */
  async init() {
    // Load current cart and rebuild variant stack
    const cart = await this.fetchCart();
    if (!cart || !cart.items) return;
    window.allOffers.cartData.cartTotal = cart.total_price / 100;
    this.variantStack = [];

    // Rebuild variant stack by adding each item's variant ID
    // multiple times based on quantity
    cart.items.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        this.variantStack.push(item.variant_id);
      }
    });

    this.storeCart(cart);
    await this.emitCartChanges();
    this.observeCartChanges();
  }

  /**
   * Fetches current cart data from Shopify's cart.js endpoint
   * Returns null if fetch fails
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js", { cache: "no-store", headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Failed to fetch cart");
      const cart = await response.json();
      this.cartCache = cart;
      this.lastFetch = Date.now();
      return cart;
    } catch (error) {
      console.error("Error fetching cart:", error);
      // Track cart fetch errors in Sentry
      if (window.CultStoreSentry) {
        window.CultStoreSentry.captureCheckoutError(error, {
          stage: 'cart_fetch',
        });
      }
      return null;
    }
  }

  /**
   * Stores cart data in localStorage and updates variant stack
   * The variant stack maintains order of added items for LIFO removal
   * when limits are exceeded
   */
  storeCart(cart) {
    if (!cart || !cart.items) return;

    localStorage.setItem("cart", JSON.stringify(cart));
    this.cartCache = cart;
    this.lastFetch = Date.now();

    // Rebuild variant stack from current cart state
    this.variantStack = [];
    cart.items.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        this.variantStack.push(item.variant_id);
      }
    });
  }

  /**
   * Retrieves cart data from localStorage
   * Returns empty cart object if no data exists or parsing fails
   */
  storedCart() {
    try {
      const cart = JSON.parse(localStorage.getItem("cart"));
      return cart || { items: [] };
    } catch (error) {
      console.error("Error parsing stored cart:", error);
      return { items: [] };
    }
  }

  /**
   * Updates cart quantities and refreshes UI
   * Takes variant ID to quantity mapping and converts to product ID updates
   * Shows error toast if update fails
   */
  async updateCart(updates) {
    try {
      // Convert variant IDs to product IDs for Shopify's update endpoint
      const productUpdates = {};
      const currentCart = await this.fetchCart();

      for (const [variantId, quantity] of Object.entries(updates)) {
        const item = currentCart.items.find(
          (i) => i.variant_id.toString() === variantId.toString(),
        );
        if (item) {
          productUpdates[item.id] = quantity;
        }
      }

      // Optimistically update UI
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ updates: productUpdates }),
      });

      if (!response.ok) throw new Error("Failed to update cart");

      const updatedCart = await response.json();
      this.cartCache = updatedCart;
      this.lastFetch = Date.now();

      return updatedCart;
    } catch (error) {
      console.error("Error updating cart:", error);
      // Track cart update errors in Sentry
      if (window.CultStoreSentry) {
        window.CultStoreSentry.captureCheckoutError(error, {
          stage: 'cart_update',
          updates: updates,
        });
      }
      window.csToast?.showToast?.(
        "error",
        "Failed to update cart. Please try again.",
      );
      return null;
    }
  }

  /**
   * Core cart limiting logic:
   * 1. Tracks item order using variant stack
   * 2. Enforces total item limit (10)
   * 3. Enforces per-product limit (3)
   * 4. Removes excess items starting with most recently added
   * Shows warning toasts when limits are exceeded
   */
  async checkCartLimits(cart) {
    if (!cart || !cart.items) return null;

    let needsUpdate = false;
    const updates = {};

    // Map variant -> product
    const variantToProduct = {};
    cart.items.forEach((item) => {
      if (item.variant_id && item.product_id) {
        variantToProduct[item.variant_id] = item.product_id;
      }
    });

    // Detect which variant was increased compared to stored cart
    let increasedVariantId = null;
    const oldCart = this.storedCart();
    if (oldCart.items?.length) {
      for (const oldItem of oldCart.items) {
        const newItem = cart.items.find(ci => ci.variant_id === oldItem.variant_id);
        if (newItem && newItem.quantity > oldItem.quantity) {
          increasedVariantId = newItem.variant_id;
          break;
        }
      }
    }

    // Sync variantStack with current quantities
    cart.items.forEach((item) => {
      if (!item.variant_id) return;
      const currentCount = this.variantStack.filter(
        (id) => id === item.variant_id,
      ).length;
      const newQty = item.quantity;

      if (newQty > currentCount) {
        for (let i = 0; i < newQty - currentCount; i++)
          this.variantStack.push(item.variant_id);
      } else if (newQty < currentCount) {
        let toRemove = currentCount - newQty;
        for (
          let i = this.variantStack.length - 1;
          i >= 0 && toRemove > 0;
          i--
        ) {
          if (this.variantStack[i] === item.variant_id) {
            this.variantStack.splice(i, 1);
            toRemove--;
          }
        }
      }
    });

    // Enforce total items limit
    if (this.variantStack.length > this.MAX_TOTAL_PRODUCTS) {
      this.variantStack = this.variantStack.slice(0, this.MAX_TOTAL_PRODUCTS);
      needsUpdate = true;
      this._toastOnce(
        "warning",
        `Your cart has reached the maximum limit. Which is ${this.MAX_TOTAL_PRODUCTS} items`,
      );
    }

    // Per-product totals from stack
    const productTotals = {};
    this.variantStack.forEach((vid) => {
      const pid = variantToProduct[vid];
      if (!pid) return;
      productTotals[pid] = (productTotals[pid] || 0) + 1;
    });

    // Reduce oldest items for products above cap with priority on removing other variants first
    Object.entries(productTotals).forEach(([productId, total]) => {
      if (total <= this.MAX_QUANTITY_PER_PRODUCT) return;
      let excess = total - this.MAX_QUANTITY_PER_PRODUCT;

      // Strict LIFO per product: scan from END of variantStack,
      // remove the newest entries that belong to this productId (any variant).
      for (let s = this.variantStack.length - 1; s >= 0 && excess > 0; s--) {
        const vid = this.variantStack[s];
        if (variantToProduct[vid] == productId) {
          this.variantStack.splice(s, 1);
          excess--;
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        this._toastOnce(
          "warning",
          `Sorry! You can only buy max ${this.MAX_QUANTITY_PER_PRODUCT} of a product.`,
        );
      }
    });

    // Final counts per variant after adjustments
    const finalCounts = {};
    this.variantStack.forEach((vid) => {
      finalCounts[vid] = (finalCounts[vid] || 0) + 1;
    });

    // Build updates for current cart items
    cart.items.forEach((item) => {
      const vid = item.variant_id;
      if (!vid) return;
      const newQty = finalCounts[vid] || 0;
      updates[vid] = newQty;
      if (newQty !== item.quantity) needsUpdate = true;
    });

    if (needsUpdate) {
      const updatedCart = await this.updateCart(updates);
      return updatedCart || cart;
    }
    return cart;
  }

  /**
   * Compares old and new cart states to determine changes
   * Tracks both complete item adds/removes and quantity changes
   * Returns arrays of added and removed items
   */
  findCartChanges(oldCart, newCart) {
    if (!oldCart || !newCart || !oldCart.items || !newCart.items) {
      return { added: [], removed: [] };
    }

    const onlyInLeft = (l, r) =>
      l.filter((li) => li.key && !r.some((ri) => ri.key && ri.key === li.key));
    let result = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
    };

    // Convert quantity changes to virtual adds/removes
    oldCart.items.forEach((oi) => {
      if (!oi.key) return;

      const ni = newCart.items.find(
        (i) => i.key === oi.key && i.quantity !== oi.quantity,
      );
      if (!ni) return;

      let quantity = ni.quantity - oi.quantity;
      let item = { ...ni };
      item.quantity = Math.abs(quantity);

      if (quantity > 0) {
        result.added.push(item);
      } else {
        result.removed.push(item);
      }
    });

    return result;
  }

  /**
   * Main event emission handler:
   * 1. Fetches latest cart state
   * 2. Enforces quantity limits
   * 3. Calculates changes from previous state
   * 4. Dispatches events with change details
   * 5. Updates stored cart state
   */
  async emitCartChanges() {
    let newCart = await this.fetchCart();
    if (!newCart) return null;

    newCart = await this.checkCartLimits(newCart);
    if (!newCart) return null;

    const oldCart = this.storedCart();
    const changes = this.findCartChanges(oldCart, newCart);

    if (
      changes.added.length > 0 ||
      changes.removed.length > 0
    ) {
      const event = new CustomEvent("cart_changed", {
        detail: {
          ...changes,
          cartItems: newCart.items,
          cartTotal: newCart.total_price / 100,
        },
      });
      window.dispatchEvent(event);
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      this.storeCart(newCart);
      return newCart;
    } else {
      console.warn("No changes detected in cart state.");
      return null;
    }
  }

  /**
   * Sets up network request monitoring:
   * Uses PerformanceObserver to watch for cart-related requests
   * Triggers change events when cart modifications are detected
   */
  observeCartChanges() {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach(async (entry) => {
        const isValidRequestType = ["xmlhttprequest", "fetch"].includes(
          entry.initiatorType,
        );
        const isCartChangeRequest = /\/cart\//.test(entry.name);

        if (isValidRequestType && isCartChangeRequest) {
          try {
            this._scheduleEmit();
          } catch (error) {
            console.error("Error handling cart change:", error);
            // Track cart observation errors in Sentry
            if (window.CultStoreSentry) {
              window.CultStoreSentry.captureCheckoutError(error, {
                stage: 'cart_observe',
              });
            }
          }
        }
      });
    });

    try {
      cartObserver.observe({ entryTypes: ["resource"] });
    } catch (error) {
      console.error("Failed to initialize cart observer:", error);
    }

    // also patch fetch / XHR so we don't miss cart mutations
    // Use a named regex for cart change detection
    const CART_CHANGE_REGEX = /\/cart\/(add|update|change|clear)\.js|\/cart(\?|$|\/)/;
    try {
      const _fetch = window.fetch;
      window.fetch = async (...args) => {
      const res = await _fetch(...args);
      try {
        const req = args[0];
        const url = typeof req === "string" ? req : (req && req.url) || "";
        // Use regex and conditional on separate lines for clarity
        if (CART_CHANGE_REGEX.test(url)) {
        this._scheduleEmit();
        }
      } catch {}
      return res;
      };
    } catch {}

    try {
      const _open = XMLHttpRequest.prototype.open;
      const _send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__cart_url = url;
      return _open.call(this, method, url, ...rest);
      };
      XMLHttpRequest.prototype.send = function(...args) {
      this.addEventListener("loadend", () => {
        try {
        if (this.__cart_url && CART_CHANGE_REGEX.test(this.__cart_url)) {
          window.__cartWatcher?._scheduleEmit?.();
        }
        } catch {}
      });
      return _send.apply(this, args);
      };
      // expose instance for XHR callback
      window.__cartWatcher = this;
    } catch {}
    document.addEventListener("cart:updated", () => this._scheduleEmit(), { passive: true });
    document.addEventListener("dispatch:cart-drawer:refresh", () => this._scheduleEmit(), { passive: true });
  }

  // internal helpers (no new comments added to keep your file's comment style)
  _scheduleEmit() {
    if (this._enforceTimer) clearTimeout(this._enforceTimer);
    this._enforceTimer = setTimeout(() => {
      this._enforceTimer = null;
      this.emitCartChanges().catch(e => console.error(e));
    }, 60);
  }
  _toastOnce(type, msg) {
    const now = Date.now();
    if (now - this._lastToastAt > this._TOAST_COOLDOWN_MS) {
      window.csToast?.showToast?.(type, msg);
      this._lastToastAt = now;
    }
  }
}

// Create and initialize cart watcher instance when page loads
document.addEventListener("DOMContentLoaded", async () => {
  const cartWatcher = new CartWatcher();
  await cartWatcher.init();
});
